import React from 'react';
import PropTypes from 'prop-types';
import { WeezHeader, FilterSetController, Card, EmptyState, Table, Icon, DropdownItem, Paginator, Tooltip, Numbers, Statistics, Message, ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/browser';
import DropdownPageSize from '../manager/components/dropdownPageSize';
import TransfersModel, { TransfersStateModel } from '../../models/transferModel';
import { InfoTextToolTipMemo } from '../attendee/list/components/attendee-row/attendee-row';
import Style from '../attendee/list/list.module.css';

import './transfers.scss';
import SortDropdown from '../../components/sortDropdown';
import i18n from 'i18next';
import MessageValidationBank from './messageValidationBank';
import { useHistory } from 'react-router-dom';
import TransfersStatusModel from '../../models/transferStatusModel';
import LoadingTable from '../../components/loadingTable';
import download from '../../utils/asynchronousDownload';

export default function Transfers({ organization }) {
    const toast = React.useContext(ToastContext);
    const locale = i18n.language;
    const { t } = useTranslation();
    const history = useHistory();

    let [paginationTransfers, setPaginationTransfers] = React.useState({
        search: null,
        size: 50,
        offset: 0,
        totalCount: 0
    });

    const [filters, setFilters] = React.useState({ orderBy: 'periode_end DESC' });
    const [transfers, setTransfers] = React.useState([]);
    const [isSearching, setIsSearching] = React.useState(true);
    const [isSearchingState, setIsSearchingState] = React.useState(true);
    const [isSearchingStatus, setIsSearchingStatus] = React.useState(true);

    const [pageSize, setPageSize] = React.useState(50);
    const [inputSearchValue, setInputSearchValue] = React.useState('');
    const [countTransfers, setCountTransfers] = React.useState(0);
    const searchTimeout = React.useRef(null);

    const [status, ,] = TransfersStatusModel.useApiModel(
        {
            organizationId: organization.id,
            launch: Boolean(isSearchingStatus),
            onSuccess: event => {
                setIsSearchingStatus(false);
            }
        },
        [isSearchingStatus, organization]
    );

    const [state, ,] = TransfersStateModel.useApiModel(
        {
            organizationId: organization.id,
            launch: Boolean(isSearchingState),
            onSuccess: response => {
                setIsSearchingState(false);
            },
            onError: e => {
                Sentry.captureException(e);
                toast.error(t('ticket.event.list.error'));
            }
        },
        [isSearchingState, organization]
    );

    const getQueryTransfers = React.useCallback(() => {
        let param_object = { orderBy: filters.orderBy };
        if (inputSearchValue !== '') {
            param_object.search = inputSearchValue;
        }

        if (inputSearchValue) {
            param_object['search'] = inputSearchValue;
        }

        param_object['size'] = pageSize;
        param_object['offset'] = paginationTransfers.offset * pageSize;

        return param_object;
    }, [filters, inputSearchValue, paginationTransfers, pageSize]);

    const [, loadingFetchEvents] = TransfersModel.useApiModel(
        {
            organizationId: organization.pk,
            query: getQueryTransfers(),
            launch: Boolean(isSearching),
            onSuccess: response => {
                setTransfers(transfers ? transfers.concat(response.items || []) : response.items || []);
                setCountTransfers(parseInt(response?.__meta?.headers?.get('total-count')));
                setIsSearching(false);
            },
            onError: e => {
                Sentry.captureException(e);
                toast.error(t('ticket.event.list.error'));
            }
        },
        [organization, isSearching, organization, setTransfers, setCountTransfers, getQueryTransfers]
    );

    const downloadTransfer = React.useCallback(
        (transferId, eventId) => {
            const model = new TransfersModel({});
            const fetchUrl = model.getUrl({ organizationId: organization.pk, transferId });
            download(() => fetchUrl, { organizationId: organization.pk, eventId }, toast, t);
        },
        [organization, toast, t]
    );

    let resetPagination = React.useCallback(() => {
        setPaginationTransfers({
            search: paginationTransfers.search,
            size: pageSize,
            offset: 0
        });
        setTransfers([]);
        setCountTransfers(0);
    }, [setPaginationTransfers]);

    let handleSearch = React.useCallback(
        (synth, value) => {
            setInputSearchValue(value);
            resetPagination();
            setIsSearching(true);
        },
        [searchTimeout, clearTimeout, setInputSearchValue, setTransfers, resetPagination]
    );

    let handleReset = React.useCallback(() => {
        handleSearch('');
    });

    const updateFilters = React.useCallback(
        (name, value) => {
            setFilters(filters => {
                let previousFilters = { ...filters };
                if (name === 'reset') {
                    resetPagination();
                    setIsSearching(true);
                    return {};
                }
                if (!value) {
                    delete previousFilters[name];
                    resetPagination();
                    setIsSearching(true);
                    return previousFilters;
                }
                let newFilters = {
                    ...previousFilters,
                    [name]: value
                };
                resetPagination();
                setIsSearching(true);
                return newFilters;
            });
        },
        [inputSearchValue, resetPagination]
    );

    const label = [
        { label: t('ticket.accounting.filter.eventIdLabel'), name: 'ev.id_evenement' },
        { label: t('ticket.transfers.date'), name: 'periode_end', tooltip: t('ticket.transfers.tooltips') },
        { label: t('ticket.transfers.event'), name: 'nom' },
        { label: t('ticket.transfers.startDate'), name: 'date_debut' },
        { label: t('ticket.transfers.amount'), name: 'montant' }
    ];

    const translateTypeMessage = str => {
        if (str === 'alert') {
            return 'danger';
        }
        return str;
    };

    const sendMail = React.useCallback(() => {
        organization
            .resend_bank_mail()
            .then(({ response }) => {
                toast.success(t(`ticket.organiser.banking.sendMailSuccess`));
            })
            .catch(error => {
                toast.error(t(`ticket.organiser.banking.sendMailError`));
            });
    }, [organization.id]);

    return (
        <>
            <WeezHeader title={t('ticket.transfers.title')}>{t('ticket.transfers.subTitle')}</WeezHeader>
            {status && !status?.missing_bankdata?.status && !status?.status && status?.bankdata_validation?.status && (
                <MessageValidationBank sendMail={sendMail}></MessageValidationBank>
            )}
            {status && !status?.status && status?.mod_warning?.status && (
                <Message
                    title={status.mod_warning.message.title}
                    type={translateTypeMessage(status.mod_warning.message.type)}
                    seeMoreLabel={t('ticket.common.seeMoreDetails')}
                    seeLessLabel={t('ticket.common.seeLessDetails')}
                    seeMore={Object.keys(status.mod_warning.message.details).map(key => {
                        return (
                            <div key={key}>
                                <span>{key}</span>
                                <ul>
                                    {status.mod_warning.message.details[key].map((str, index) => (
                                        <li key={index}>{str}</li>
                                    ))}
                                </ul>
                            </div>
                        );
                    })}
                >
                    {<div dangerouslySetInnerHTML={{ __html: status.mod_warning.message.body }}></div>}
                </Message>
            )}

            {status && !status?.status && status?.end_of_event?.status && (
                <Message
                    title={status.end_of_event.message.title}
                    type={translateTypeMessage(status.end_of_event.message.type)}
                    seeMoreLabel={t('ticket.common.seeMoreDetails')}
                    seeLessLabel={t('ticket.common.seeLessDetails')}
                    seeMore={status.end_of_event.message.details}
                >
                    {<div dangerouslySetInnerHTML={{ __html: status.end_of_event.message.body }}></div>}
                </Message>
            )}

            {status && !status?.status && status?.missing_bankdata?.status && (
                <Message
                    title={t('ticket.transfers.messageMissingBankData.title')}
                    type={translateTypeMessage(status.missing_bankdata.message.type)}
                    seeMoreLabel={t('ticket.common.seeMoreDetails')}
                    seeLessLabel={t('ticket.common.seeLessDetails')}
                    seeMore={status.missing_bankdata.message.details}
                    links={[
                        {
                            onClick: () => history.push(`/ticket/O${organization.id}/organization/bank`),
                            label: t('ticket.transfers.messageMissingBankData.link')
                        }
                    ]}
                >
                    <p>{t('ticket.transfers.messageMissingBankData.body')}</p>
                </Message>
            )}

            {!isSearchingState && (
                <Statistics.Group className={'contentState'}>
                    <Statistics
                        helpLabel={t('ticket.transfers.toolTips.latest_period')}
                        subtitle={t('ticket.transfers.statsSub.total15')}
                        stat={
                            <Numbers
                                className={Style['statistics-number']}
                                value={state?.latest_period?.replace(/\s/g, '')}
                                locale={locale}
                                currency={organization.currency}
                                showNullDecimal
                            />
                        }
                    />
                    <Statistics
                        helpLabel={t('ticket.transfers.toolTips.previous_30_days')}
                        subtitle={t('ticket.transfers.statsSub.total30')}
                        stat={
                            <Numbers
                                className={Style['statistics-number']}
                                value={state?.previous_30_days?.replace(/\s/g, '')}
                                locale={locale}
                                currency={organization.currency}
                                showNullDecimal
                            />
                        }
                    />
                    <Statistics
                        helpLabel={t('ticket.transfers.toolTips.current_year')}
                        subtitle={t('ticket.transfers.statsSub.total')}
                        stat={
                            <Numbers
                                className={Style['statistics-number']}
                                value={state?.current_year?.replace(/\s/g, '')}
                                locale={locale}
                                currency={organization.currency}
                                showNullDecimal
                            />
                        }
                    />
                </Statistics.Group>
            )}

            <Card>
                <Card.Content>
                    <FilterSetController
                        onChange={values => {
                            for (const [key, value] of Object.entries(values)) {
                                updateFilters(key, value);
                            }
                        }}
                        search={{
                            onChange: (synth, value) => {
                                handleSearch(synth, value);
                            },
                            handleReset: () => {
                                handleReset('');
                            },
                            placeholder: t('ticket.accounting.filter.searchBarPlaceholder'),
                            value: inputSearchValue
                        }}
                        reset={() => {
                            setFilters({});
                        }}
                        total={countTransfers}
                        isLoadingTotal={loadingFetchEvents}
                        labelTotal={t('ticket.transfers.payment', { count: countTransfers })}
                    />
                    <div className="dropdown">
                        <DropdownPageSize
                            current={pageSize}
                            onChange={e => {
                                setPaginationTransfers({
                                    search: paginationTransfers.search,
                                    size: e,
                                    offset: 0
                                });
                                setPageSize(e);
                                setTransfers([]);
                                setIsSearching(true);
                            }}
                            totalCount={countTransfers}
                            loading={loadingFetchEvents}
                            labelItem="ticket.accounting.pageSize"
                        />
                    </div>
                </Card.Content>
                <Table useNew className={(Style['list__table'], 'transferTable')}>
                    <Table.THead>
                        <Table.Tr isHeader>
                            {label.map((obj, key) => {
                                return transfers.length === 0 ? (
                                    <th>{obj.label}</th>
                                ) : (
                                    <SortDropdown
                                        tooltip={obj.tooltip}
                                        name={obj.name}
                                        key={key}
                                        label={obj.label}
                                        ordering={filters.orderBy.split(' ')[1] === 'ASC' ? filters.orderBy.split(' ')[0] : '-' + filters.orderBy.split(' ')[0]}
                                        items={[
                                            <DropdownItem.Icons
                                                key={1}
                                                item={
                                                    <div className="contentDropDownItem">
                                                        <div className={filters.orderBy === obj.name + ' ASC' ? 'colorBlue' : ''}>{t('ticket.common.ordering.ascending')}</div>
                                                        {filters.orderBy === obj.name + ' ASC' ? <Icon name="check"></Icon> : null}
                                                    </div>
                                                }
                                                onClick={() => updateFilters('orderBy', obj.name + ' ASC')}
                                            />,
                                            <DropdownItem.Icons
                                                key={2}
                                                item={
                                                    <div className="contentDropDownItem">
                                                        <div className={filters.orderBy === obj.name + ' DESC' ? 'colorBlue' : ''}>{t('ticket.common.ordering.descending')}</div>
                                                        {filters.orderBy === obj.name + ' DESC' ? <Icon color="ticket" name="check"></Icon> : null}
                                                    </div>
                                                }
                                                onClick={() => updateFilters('orderBy', obj.name + ' DESC')}
                                            />
                                        ]}
                                    />
                                );
                            })}
                            <th className="center" style={{ width: '100px' }}>
                                {t('ticket.transfers.supportingDocuments')}
                            </th>
                        </Table.Tr>
                    </Table.THead>
                    <Table.TBody>
                        {transfers.map((value, key) => (
                            <Table.Tr key={key}>
                                <Table.Td>{value.id_evenement ? <InfoTextToolTipMemo title={value.id_evenement} /> : '-'}</Table.Td>
                                <Table.Td>{value.date ? <InfoTextToolTipMemo title={value.date} /> : '-'}</Table.Td>
                                <Table.Td>{value.events ? <InfoTextToolTipMemo title={value.events} /> : '-'}</Table.Td>
                                <Table.Td>{value.date_evenement ? <InfoTextToolTipMemo title={value.date_evenement} /> : '-'}</Table.Td>
                                <Table.Td>
                                    {value.montant ? (
                                        <Numbers showNullDecimal={true} value={parseFloat(value.montant).toFixed(2) || 0} locale={locale} currency={organization?.currency} />
                                    ) : (
                                        '-'
                                    )}
                                </Table.Td>
                                <Table.Td>
                                    <Tooltip visible={true} triggerOnHover={true} title={t('ticket.transfers.hoverDownload')}>
                                        <Icon
                                            onClick={e => {
                                                downloadTransfer(value.id_versement, value.id_evenement);
                                            }}
                                            name="download"
                                            size="Regular"
                                        />
                                    </Tooltip>
                                </Table.Td>
                            </Table.Tr>
                        ))}
                    </Table.TBody>
                </Table>
                <LoadingTable loading={loadingFetchEvents} />
                {!loadingFetchEvents && transfers.length === 0 && (
                    <EmptyState icon={<Icon name="list" />} title={t('ticket.transfers.noPayment')} subtitle={t('ticket.transfers.subNoPayment')} />
                )}
                <div className="paginator">
                    <Paginator
                        currentPage={paginationTransfers.offset}
                        onPageChange={numberPage => {
                            setPaginationTransfers({
                                search: paginationTransfers.search,
                                size: paginationTransfers.size,
                                offset: numberPage
                            });
                            setTransfers([]);
                            setIsSearching(true);
                        }}
                        totalCount={countTransfers}
                        pageSize={pageSize}
                    />
                </div>
            </Card>
        </>
    );
}

Transfers.propTypes = {
    organization: PropTypes.object.isRequired
};
